/**
 * Memoize a promise DERIVED from other promises so that repeated renders hand
 * `<Await>` / `<Suspense>` the SAME promise object.
 *
 * COM-405 / COM-406 (release 15): `Breadcrumbs` derived
 * `Promise.all([categorySlugMap, categoryAltUrlMap]).then(...)` inline in
 * render whenever the layout loader streamed those maps as promises. Every
 * render minted a NEW pending promise, and React Router's `<Await>` only
 * short-circuits on a promise it has already tracked (`_tracked` / `_data`),
 * so each render threw a fresh pending promise. Inside a `startTransition`
 * router-state update over an already-visible Suspense boundary React does not
 * show the fallback — it holds the whole transition, waits for the promise,
 * re-renders from the root on the ping, gets ANOTHER fresh promise, and loops
 * (measured: ~1,350 breadcrumb renders in 3 s on the RC). Because every router
 * state update rides that same transition lane, nothing router-driven ever
 * committed again: add-to-cart buttons stayed disabled, later adds never opened
 * the modal, the mini-cart would not open. Sync updates (typing, Radix
 * triggers) still worked, which is why the page "looked alive".
 *
 * Why not `useMemo`: when a transition suspends, React discards that render's
 * work-in-progress — including the memo — and the retry re-runs the component
 * with the LAST COMMITTED memo, whose deps no longer match, so it recomputes
 * and mints another promise. The cache has to live outside React and be keyed
 * by the identity of the source promises, which is what this does.
 *
 * Contract: the returned function is pure with respect to argument identity —
 * the same argument identities return the same result object. The WeakMap is
 * keyed on the first object-typed argument (a promise is an object), and every
 * argument is compared by identity, so a loader revalidation (new promises)
 * derives once, and every render in between reuses it. Plain-value inputs are
 * passed straight to `derive` (nothing to key on, nothing to memoize).
 */
export function memoizeDerivedPromise<A extends readonly unknown[], R>(
  derive: (...args: A) => R,
): (...args: A) => R {
  const cache = new WeakMap<object, { args: readonly unknown[]; result: R }>();
  return (...args: A): R => {
    const key = args.find(
      (a): a is object =>
        (typeof a === "object" && a !== null) || typeof a === "function",
    );
    if (!key) return derive(...args);
    const hit = cache.get(key);
    if (
      hit &&
      hit.args.length === args.length &&
      hit.args.every((v, i) => v === args[i])
    ) {
      return hit.result;
    }
    const result = derive(...args);
    cache.set(key, { args, result });
    return result;
  };
}
